import {
  html,
  attr,
  prop,
  Signal,
  When,
  Value,
  Fragment,
  on,
  Async,
} from '@tempots/dom'
import { Anchor } from '@tempots/ui'
import { Icon } from '@tempots/beatui'
import { PageLayout } from '@/components/layout'
import { DataTable } from '@/components/data'
import { LoadingState } from '@/components/ui/loading-state'
import { ErrorState } from '@/components/ui/error-state'
import { StatusBadge, type BadgeVariant } from '@/components/ui/status-badge'
import { createProjectJobs } from '@/services/gateway'
import type { JobsService } from '@/services/jobs.service'
import type { JobResponse, JobStatus } from '@/services/types'
import { Workspace } from '@/state/workspace'
import { formatDate, capitalize, formatCredits } from '@/utils'

export interface JobsPageOptions {
  projectId: Signal<string>
}

export function JobsPage({ projectId }: JobsPageOptions) {
  projectId.on(Workspace.changeProject)

  const jobs = prop<JobResponse[]>([])
  const activeFilter = prop<JobStatus | 'all'>('all')

  // Create project-scoped jobs service
  let jobsService: JobsService | null = null
  let lastProjectId = ''

  const getJobsService = (): JobsService | null => {
    const pid = projectId.value
    if (!pid) return null
    if (pid !== lastProjectId) {
      jobsService = createProjectJobs(pid)
      lastProjectId = pid
    }
    return jobsService
  }

  // Load jobs from API
  const loadJobs = async () => {
    const service = getJobsService()
    if (!service) {
      throw new Error('No project selected')
    }

    const filter = activeFilter.get()
    const options = filter === 'all' ? {} : { status: filter as JobStatus }
    const response = await service.list(options)
    jobs.set(response.jobs)
    return response.jobs
  }

  // Initial load promise
  const initialLoad = loadJobs()

  // Filter handler - reloads data
  const setFilter = (status: JobStatus | 'all') => {
    activeFilter.set(status)
    loadJobs()
  }

  return PageLayout({
    header: 'Jobs',
    content: Async(initialLoad, {
      pending: () => LoadingState({ message: 'Loading jobs...', size: 'lg' }),
      error: (err) =>
        ErrorState({
          error: prop<string | null>(
            err instanceof Error ? err.message : 'Failed to load jobs'
          ),
        }),
      then: () =>
        Fragment(
          // Header with stats
          html.div(
            attr.class(
              'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'
            ),
            html.p(
              attr.class('text-sm text-gray-600'),
              'Track the status of your file processing jobs'
            ),
            // Status filter
            html.div(
              attr.class('flex items-center gap-2'),
              FilterButton('all', activeFilter, setFilter),
              FilterButton('pending', activeFilter, setFilter),
              FilterButton('processing', activeFilter, setFilter),
              FilterButton('completed', activeFilter, setFilter),
              FilterButton('failed', activeFilter, setFilter)
            )
          ),

          // Table/Cards
          html.div(
            attr.class(
              'bg-white rounded-xl border border-gray-200 overflow-hidden'
            ),
            DataTable<JobResponse>({
              columns: [
                {
                  key: 'actionName',
                  header: 'Action',
                  render: (item) =>
                    html.div(
                      attr.class('flex items-center gap-3'),
                      html.div(
                        attr.class(
                          'w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center'
                        ),
                        Icon(
                          { icon: 'mdi:lightning-bolt' },
                          attr.class('w-5 h-5 text-gray-600')
                        )
                      ),
                      html.div(
                        html.p(
                          attr.class('font-medium text-gray-900'),
                          item.map((j) => j.actionName)
                        ),
                        html.p(
                          attr.class('text-sm text-gray-500'),
                          item.map((j) =>
                            j.fileHashes.length > 0
                              ? `${j.fileHashes.length} file(s)`
                              : 'No files'
                          )
                        )
                      )
                    ),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (item) => JobStatusBadge(item.map((j) => j.status)),
                },
                {
                  key: 'creditsCharged',
                  header: 'Credits',
                  render: (item) =>
                    html.span(
                      attr.class('text-sm text-gray-500'),
                      item.map((j) =>
                        j.creditsCharged
                          ? formatCredits(-j.creditsCharged, {
                              includeSign: true,
                            })
                          : '-'
                      )
                    ),
                },
                {
                  key: 'createdAt',
                  header: 'Started',
                  render: (item) =>
                    html.span(
                      attr.class('text-gray-500 text-sm'),
                      item.map((j) => formatDate(j.createdAt))
                    ),
                },
                {
                  key: 'id',
                  header: '',
                  render: (item) =>
                    Anchor(
                      { href: item.map((j) => `/jobs/${j.id}`) },
                      attr.class(
                        'text-brand-600 hover:text-brand-700 text-sm font-medium'
                      ),
                      'View'
                    ),
                },
              ],
              data: jobs,
              renderMobileCard: (job) => JobCard(job),
              emptyMessage: 'No jobs yet. Run an action to create one.',
            })
          )
        ),
    }),
  })
}

function FilterButton(
  status: JobStatus | 'all',
  activeFilter: Signal<JobStatus | 'all'>,
  onClick: (status: JobStatus | 'all') => void
) {
  return html.button(
    attr.type('button'),
    attr.class(
      activeFilter.map(
        (active) =>
          `px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
            active === status
              ? 'bg-brand-100 text-brand-700'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`
      )
    ),
    on.click(() => onClick(status)),
    capitalize(status)
  )
}

const jobStatusVariantMap: Record<JobStatus, BadgeVariant> = {
  pending: 'warning',
  processing: 'info',
  completed: 'success',
  failed: 'error',
}

const jobStatusIconMap: Record<JobStatus, string> = {
  pending: 'mdi:clock-outline',
  processing: 'mdi:loading',
  completed: 'mdi:check-circle',
  failed: 'mdi:alert-circle',
}

function JobStatusBadge(status: Value<JobStatus>) {
  const statusSignal = Value.toSignal(status)

  return StatusBadge({
    label: statusSignal.map((s) => capitalize(s)),
    variant: statusSignal.map((s) => jobStatusVariantMap[s] ?? 'neutral'),
    icon: statusSignal.map((s) => jobStatusIconMap[s] ?? 'mdi:help-circle'),
  })
}

function JobCard(job: Signal<JobResponse>) {
  return html.div(
    attr.class('bg-white border border-gray-200 rounded-lg p-4'),
    html.div(
      attr.class('flex items-start justify-between'),
      html.div(
        attr.class('flex items-center gap-3'),
        html.div(
          attr.class(
            'w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center'
          ),
          Icon(
            { icon: 'mdi:lightning-bolt' },
            attr.class('w-5 h-5 text-gray-600')
          )
        ),
        html.div(
          html.h3(
            attr.class('font-medium text-gray-900'),
            job.map((j) => j.actionName)
          ),
          html.p(
            attr.class('text-sm text-gray-500'),
            job.map((j) =>
              j.fileHashes.length > 0
                ? `${j.fileHashes.length} file(s)`
                : 'No files'
            )
          )
        )
      ),
      Anchor(
        { href: job.map((j) => `/jobs/${j.id}`) },
        attr.class('text-brand-600 hover:text-brand-700'),
        Icon({ icon: 'mdi:chevron-right' }, attr.class('w-5 h-5'))
      )
    ),
    html.div(
      attr.class('mt-3 flex items-center justify-between'),
      JobStatusBadge(job.map((j) => j.status)),
      html.span(
        attr.class('text-xs text-gray-500'),
        job.map((j) => formatDate(j.createdAt))
      )
    ),
    When(
      job.map(
        (j) => j.error !== undefined && j.error !== null && j.error !== ''
      ),
      () =>
        html.p(
          attr.class('mt-2 text-sm text-red-600'),
          job.map((j) => j.error!)
        )
    )
  )
}
